// import React, { useState } from 'react';
// import { faqs } from '../data/faqs';

// const Faqs: React.FC = () => {
//   const [openIndex, setOpenIndex] = useState<number | null>(null);

//   return (
//     <div className="font-sans bg-lightGrey min-h-screen py-12 px-4">
//       <div className="max-w-3xl mx-auto bg-white rounded-xl shadow-lg p-8">
//         <h1 className="font-heading text-3xl font-bold text-darkBlue mb-8 text-center">Frequently Asked Questions</h1>
//         <div className="space-y-4">
//           {faqs.map((faq, idx) => (
//             <div key={idx} className="border-b border-lightTeal pb-4">
//               <button
//                 className="w-full text-left font-heading text-lg text-coral focus:outline-none flex justify-between items-center"
//                 onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
//                 aria-expanded={openIndex === idx}
//               >
//                 {faq.question}
//                 <span className="ml-2 text-darkBlue">{openIndex === idx ? '-' : '+'}</span>
//               </button>
//               {openIndex === idx && (
//                 <div className="mt-2 text-darkBlue font-sans text-base">
//                   {faq.answer}
//                 </div>
//               )}
//             </div>
//           ))}
//         </div>
//       </div>
//     </div>
//   );
// };

// export default Faqs;

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { faqs } from '../data/faqs';

const Faqs: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  // Group FAQs by category (you can modify this based on your data structure)
  const faqCategories = [
    {
      title: "General Information",
      icon: "ℹ️",
      color: "brand-primary",
      faqs: faqs.slice(0, Math.ceil(faqs.length / 3))
    },
    {
      title: "Services & Appointments",
      icon: "🏥",
      color: "brand-coral",
      faqs: faqs.slice(Math.ceil(faqs.length / 3), Math.ceil(faqs.length * 2 / 3))
    },
    {
      title: "Billing & Insurance",
      icon: "💳",
      color: "brand-teal",
      faqs: faqs.slice(Math.ceil(faqs.length * 2 / 3))
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-brand-primary/5 via-white to-brand-coral/5 py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="text-sm font-medium text-brand-primary mb-4">
              <span className="inline-block px-3 py-1 bg-white rounded-full border border-gray-100 shadow-sm">
                Help Center
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-brand-primary mb-6">
              Frequently Asked
              <br />
              <span className="text-brand-coral">Questions</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              Find answers to common questions about our healthcare services, appointments, 
              billing, and more. Can't find what you're looking for? Contact us directly.
            </p>
          </motion.div>
        </div>
      </div>

      {/* FAQ Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Quick Search Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-12"
        >
          <div className="relative max-w-xl mx-auto">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <span className="text-gray-400">🔍</span>
            </div>
            <input
              type="text"
              className="block w-full pl-10 pr-3 py-4 border border-gray-200 rounded-lg leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-2 focus:ring-brand-primary focus:border-brand-primary text-brand-primary"
              placeholder="Search frequently asked questions..."
            />
          </div>
        </motion.div>

        {/* FAQ Categories */}
        <div className="space-y-12">
          {faqCategories.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 + categoryIndex * 0.1 }}
              className="space-y-6"
            >
              {/* Category Header */}
              <div className="flex items-center space-x-4 mb-8">
                <div className={`w-12 h-12 bg-${category.color} rounded-xl flex items-center justify-center text-white text-xl shadow-lg`}>
                  {category.icon}
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-brand-primary">{category.title}</h2>
                  <p className="text-gray-600">Common questions in this category</p>
                </div>
              </div>

              {/* FAQ Items */}
              <div className="space-y-4">
                {category.faqs.map((faq, faqIndex) => {
                  const globalIndex = categoryIndex * Math.ceil(faqs.length / 3) + faqIndex;
                  const isOpen = openIndex === globalIndex;
                  
                  return (
                    <motion.div
                      key={faqIndex}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.3, delay: faqIndex * 0.05 }}
                      className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300"
                    >
                      <button
                        className="w-full px-6 py-5 text-left focus:outline-none focus:ring-2 focus:ring-brand-primary focus:ring-inset rounded-xl"
                        onClick={() => toggleFaq(globalIndex)}
                        aria-expanded={isOpen}
                      >
                        <div className="flex justify-between items-center">
                          <h3 className="text-lg font-semibold text-brand-primary pr-8 leading-relaxed">
                            {faq.question}
                          </h3>
                          <motion.div
                            animate={{ rotate: isOpen ? 45 : 0 }}
                            transition={{ duration: 0.2 }}
                            className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 bg-${category.color}/10`}
                          >
                            <span className={`text-${category.color} font-bold text-xl`}>+</span>
                          </motion.div>
                        </div>
                      </button>
                      
                      <AnimatePresence>
                        {isOpen && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden"
                          >
                            <div className="px-6 pb-6">
                              <div className={`w-full h-px bg-${category.color}/20 mb-4`}></div>
                              <div className="text-gray-700 leading-relaxed space-y-3">
                                {typeof faq.answer === 'string' ? (
                                  <p>{faq.answer}</p>
                                ) : (
                                  faq.answer
                                )}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Contact CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-20 text-center"
        >
          <div className="bg-gradient-to-r from-brand-primary to-brand-teal rounded-2xl p-8 text-white">
            <h3 className="text-2xl font-bold mb-4">Still have questions?</h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto leading-relaxed">
              Our healthcare team is here to help. Contact us directly for personalized assistance 
              with your healthcare needs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="/contact"
                className="px-8 py-3 bg-black text-brand-primary font-semibold rounded-lg hover:bg-black-50 transition-all duration-300 transform hover:-translate-y-0.5 shadow-lg"
              >
                Contact Us
              </a>
              <a
                href="tel:+14695730176"
                className="px-8 py-3 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-brand-primary transition-all duration-300"
              >
                Call Now
              </a>
            </div>
          </div>
        </motion.div>

        {/* Quick Links */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-16 grid md:grid-cols-3 gap-6"
        >
          <div className="bg-gradient-to-br from-brand-coral/5 to-brand-yellow/5 p-6 rounded-xl border border-brand-coral/20 text-center">
            <div className="w-12 h-12 bg-brand-coral rounded-lg flex items-center justify-center text-white text-xl mx-auto mb-4">
              📅
            </div>
            <h4 className="text-lg font-semibold text-brand-primary mb-2">Book Appointment</h4>
            <p className="text-gray-600 mb-4">Schedule your visit with our healthcare professionals</p>
            <a href="/contact" className="text-brand-coral font-medium hover:underline">
              Book Now →
            </a>
          </div>

          <div className="bg-gradient-to-br from-brand-teal/5 to-brand-blue/5 p-6 rounded-xl border border-brand-teal/20 text-center">
            <div className="w-12 h-12 bg-brand-teal rounded-lg flex items-center justify-center text-white text-xl mx-auto mb-4">
              🏥
            </div>
            <h4 className="text-lg font-semibold text-brand-primary mb-2">Our Services</h4>
            <p className="text-gray-600 mb-4">Learn about our comprehensive healthcare services</p>
            <a href="/services" className="text-brand-teal font-medium hover:underline">
              View Services →
            </a>
          </div>

          <div className="bg-gradient-to-br from-brand-magenta/5 to-brand-coral/5 p-6 rounded-xl border border-brand-magenta/20 text-center">
            <div className="w-12 h-12 bg-brand-magenta rounded-lg flex items-center justify-center text-white text-xl mx-auto mb-4">
              👨‍⚕️
            </div>
            <h4 className="text-lg font-semibold text-brand-primary mb-2">Meet Our Team</h4>
            <p className="text-gray-600 mb-4">Get to know our experienced healthcare professionals</p>
            <a href="/jobs" className="text-brand-magenta font-medium hover:underline">
              Meet Team →
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Faqs;